import api from "services/api";

export const fetchUsers = async (
  pageSize = 10,
  pageNumber = 1,
  searchTerm = null,
  sortColumn = null,
  sortDirection = "ASC",
  isActive = null
) => {
  try {
    let url = `/User?pageSize=${pageSize}&pageNumber=${pageNumber}`;

    if (searchTerm) {
      url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    }

    if (sortColumn) {
      url += `&sortColumn=${sortColumn}&sortDirection=${sortDirection}`;
    } // The isActive parameter needs to be converted to lowercase for proper binding in .NET
    if (isActive !== null && isActive !== undefined) {
      // Log detailed debugging information

      // Make sure isActive is cast to an explicit boolean and converted to lowercase
      const boolValue = isActive === true ? "true" : "false";
      url += `&isActive=${boolValue}`;
    }

    const response = await api.get(url);
    // Log specifically what the backend returned for this filter
    if (isActive !== null && isActive !== undefined) {

    }

    // Make sure we have a valid response with proper structure
    if (!response.data) {
      console.error("API response is missing data property:", response);
      return { items: [], totalItems: 0 };
    }

    // Handle the backend API format which returns { success, message, data, totalCount } structure
    if (
      response.data.success !== undefined &&
      Array.isArray(response.data.data)
    ) {
      return {
        items: response.data.data,
        totalItems: response.data.totalCount,
      };
    }

    // If items is missing, create a default structure
    if (!response.data.items && Array.isArray(response.data)) {
      // The API returned an array directly
      return {
        items: response.data,
        totalItems: response.data.length,
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    // Return empty data structure on error
    return { data: { items: [], totalItems: 0 } };
  }
};

export const getUserById = async (userId) => {
  try {
    const response = await api.get(`/User/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/User/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${userId}:`, error);
    throw error;
  }
};

export const activateUser = async (userId, isActive) => {
  try {
    // Sử dụng phương thức PATCH thay vì POST theo yêu cầu của API
    // URL đúng theo Swagger là /api/Admin/users/{userId}/activate
    const response = await api.patch(`/Admin/users/${userId}/activate`, {
      isActive,
    });

    return response.data;
  } catch (error) {
    console.error(
      `Error ${isActive ? "activating" : "deactivating"} user ${userId}:`,
      error
    );
    throw error;
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await api.post("/Auth/register", userData);
    return response.data;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

export const uploadAvatar = async (userId, formData) => {
  try {
    const response = await api.post(`/User/${userId}/avatar`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error uploading avatar for user ${userId}:`, error);
    throw error;
  }
};

export const removeAvatar = async (userId) => {
  try {
    // Thêm log để debug
    // Đảm bảo userId là một số
    const numericUserId = parseInt(userId);
    if (isNaN(numericUserId)) {
      throw new Error(`Invalid user ID: ${userId}`);
    }
    // Gọi API với userId đã được validate - sử dụng đúng cú pháp là "User" (chữ U viết hoa)
    // để khớp với định nghĩa Route trong UserController.cs [Route("api/[controller]")]
    const response = await api.delete(`/User/${numericUserId}/avatar`);
    return response.data;
  } catch (error) {
    console.error(`Error removing avatar for user ${userId}:`, error);
    throw error;
  }
};

export const changePassword = async (oldPassword, newPassword) => {
  try {
    const response = await api.post("/User/change-password", {
      oldPassword,
      newPassword,
    });
    return response.data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const resetUserPassword = async (userId, newPassword) => {
  // This is an admin-only operation to reset a user's password
  try {
    const response = await api.post(`/User/${userId}/reset-password`, {
      newPassword,
    });
    return response.data;
  } catch (error) {
    console.error(`Error resetting password for user ${userId}:`, error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    // Lấy tất cả user với page size lớn để có thể sử dụng trong dropdown filter
    const response = await fetchUsers(1000, 1, null, "FullName", "ASC", true);
    return { data: response.items || [] };
  } catch (error) {
    console.error("Error fetching all users:", error);
    return { data: [] };
  }
};

// Export userService object for easier import
export const userService = {
  fetchUsers,
  getUserById,
  updateUser,
  activateUser,
  registerUser,
  uploadAvatar,
  removeAvatar,
  changePassword,
  resetUserPassword,
  getAllUsers,
};
